import { ColumnDefinition } from "shared/components/UniversalTable/types/ColumnConfig";

/**
 * Building Financials Table Configuration - Simplified Version
 * Shows essential financial metrics without duplicate year columns
 */

export const BUILDING_FINANCIALS_COLUMNS: ColumnDefinition[] = [
  // Selection checkbox (sticky)
  {
    id: "checkbox",
    label: "",
    minWidth: 50,
    defaultWeight: 1,
    sticky: true,
  },

  // Basic building info (sticky)
  {
    id: "id",
    label: "ID",
    sortKey: "id",
    minWidth: 120,
    defaultWeight: 2,
    sticky: true,
  },
  {
    id: "address",
    label: "Adres",
    sortKey: "googlePlaceLongAddress",
    minWidth: 250,
    defaultWeight: 4,
  },
  {
    id: "company",
    label: "Bedrijf",
    sortKey: "company.name",
    minWidth: 150,
    defaultWeight: 3,
  },

  // ===== AANKOOP GEGEVENS =====
  {
    id: "acquisitionDate",
    label: "Datum aankoop",
    minWidth: 140,
    defaultWeight: 2,
  },
  {
    id: "totalBid",
    label: "Vastgoedprijs (excl. kosten)",
    minWidth: 180,
    defaultWeight: 2,
  },
  {
    id: "transactionCosts",
    label: "Transactiekosten",
    minWidth: 150,
    defaultWeight: 2,
  },
  {
    id: "brokerCostsNet",
    label: "Makelaarskosten",
    minWidth: 150,
    defaultWeight: 2,
  },
  {
    id: "dueDiligenceCost",
    label: "Due diligence kosten",
    minWidth: 160,
    defaultWeight: 2,
  },
  {
    id: "fiscalDiscountCompanyShares",
    label: "Fisc. kort. venn.",
    minWidth: 140,
    defaultWeight: 2,
  },
  {
    id: "fiscalDiscountRealEstate",
    label: "Fisc. kort. vastgoed",
    minWidth: 150,
    defaultWeight: 2,
  },
  {
    id: "otherDiscounts",
    label: "Andere kortingen",
    minWidth: 140,
    defaultWeight: 2,
  },
  {
    id: "totalCashOutBeforeRenovation",
    label: "Cash-out excl. renov.",
    minWidth: 170,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend veld - niet bewerkbaar",
  },

  // ===== UITGESTELDE REGISTRATIEBELASTINGEN =====
  {
    id: "deferredRegistrationTaxes",
    label: "Uitgest. reg.belastingen",
    minWidth: 180,
    defaultWeight: 2,
  },
  {
    id: "deferredRegistrationDate",
    label: "Deadline nabelasting",
    minWidth: 160,
    defaultWeight: 2,
  },

  // ===== CAPEX =====
  {
    id: "renovationBudget",
    label: "Geplande capex",
    minWidth: 140,
    defaultWeight: 2,
  },
  {
    id: "renovationCostsToDate",
    label: "Capex to date",
    minWidth: 130,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit boekhouding - niet bewerkbaar",
  },

  // ===== WAARDERING =====
  {
    id: "estimatedMarketValue",
    label: "Geschatte marktwaarde",
    minWidth: 170,
    defaultWeight: 2,
  },
  {
    id: "insuredValue",
    label: "Verzekerde waarde",
    minWidth: 150,
    defaultWeight: 2,
  },

  // ===== HUURINKOMSTEN =====
  {
    id: "actualIndexedContractualRentsResidential",
    label: "Contract. huur residentieel",
    minWidth: 200,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit units - niet bewerkbaar",
  },
  {
    id: "actualResidentialRentInTerminationPhase",
    label: "Opzegfase residentieel",
    minWidth: 180,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit units - niet bewerkbaar",
  },
  {
    id: "actualIndexedContractualRentsRetail",
    label: "Contract. huur retail",
    minWidth: 170,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit units - niet bewerkbaar",
  },
  {
    id: "actualRetailRentInTerminationPhase",
    label: "Opzegfase retail",
    minWidth: 150,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit units - niet bewerkbaar",
  },
  {
    id: "actualIndexedContractualRentsOffices",
    label: "Contract. huur kantoren",
    minWidth: 180,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit units - niet bewerkbaar",
  },
  {
    id: "actualOfficeRentInTerminationPhase",
    label: "Opzegfase kantoren",
    minWidth: 170,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit units - niet bewerkbaar",
  },
  {
    id: "actualIndexedContractualRentsTotal",
    label: "Contract. huur totaal",
    minWidth: 180,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit units - niet bewerkbaar",
  },
  {
    id: "actualTotalRentInTerminationPhase",
    label: "Opzegfase totaal",
    minWidth: 150,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit units - niet bewerkbaar",
  },
  {
    id: "ervOnVacantUnits",
    label: "ERV op leegstand",
    minWidth: 150,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit units - niet bewerkbaar",
  },
  {
    id: "contractualRentsPlusErv",
    label: "Contractuele huur + ERV",
    minWidth: 180,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit units - niet bewerkbaar",
  },
  {
    id: "potentialGrossIncomeActual",
    label: "Bruto huur bij aankoop (PGI)",
    minWidth: 200,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Waarde bij aankoop - niet bewerkbaar",
  },
  {
    id: "deltaRentalValueSinceAcquisition",
    label: "Delta t.o.v. aankoop",
    minWidth: 170,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend: (Contract. huur + ERV) - PGI bij aankoop",
  },
  {
    id: "actualVacantUnits",
    label: "Actuele leegstaande units",
    minWidth: 200,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit units - niet bewerkbaar",
  },
  {
    id: "ervTotal",
    label: "ERV bij aankoop (totaal)",
    minWidth: 170,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit units - niet bewerkbaar",
  },
  {
    id: "ervResidentialUnits",
    label: "ERV residentieel",
    minWidth: 140,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit units - niet bewerkbaar",
  },
  {
    id: "ervRetailUnits",
    label: "ERV retail",
    minWidth: 120,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit units - niet bewerkbaar",
  },
  {
    id: "ervOfficeUnits",
    label: "ERV kantoor",
    minWidth: 120,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit units - niet bewerkbaar",
  },

  // ===== OPERATIONEEL (YTD) =====
  {
    id: "totalNetRentalIncome",
    label: "Netto huurink. (YTD)",
    minWidth: 170,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit boekhouding - niet bewerkbaar",
  },
  {
    id: "totalRentalIncome",
    label: "Bruto huurink. (YTD)",
    minWidth: 170,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit boekhouding - niet bewerkbaar",
  },
  {
    id: "totalProvisions",
    label: "Provisies (YTD)",
    minWidth: 160,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit boekhouding - niet bewerkbaar",
  },
  {
    id: "totalRentExpenditures",
    label: "Huurlasten (YTD)",
    minWidth: 170,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit boekhouding - niet bewerkbaar",
  },
  {
    id: "totalTaxProvisions",
    label: "Provisie OV (YTD)",
    minWidth: 160,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit boekhouding - niet bewerkbaar",
  },
  {
    id: "totalTaxExpenditures",
    label: "OV - Vastgoedbelastingen (YTD)",
    minWidth: 200,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit boekhouding - niet bewerkbaar",
  },
  {
    id: "propertyCosts",
    label: "Vastgoedkosten (YTD)",
    minWidth: 180,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend vanuit boekhouding - niet bewerkbaar",
  },
  {
    id: "marginBeforeVAT",
    label: "Operationeel resultaat (YTD)",
    minWidth: 200,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend: Netto huurinkomsten - Vastgoedkosten",
  },
  {
    id: "percentageOperationalCosts",
    label: "% Operationele kosten (YTD)",
    minWidth: 200,
    defaultWeight: 2,
    isReadOnly: true,
    readOnlyTooltip: "Berekend: (Vastgoedkosten / Netto huurinkomsten) × 100",
  },
];

/**
 * Filter sections for building financials
 */
export const BUILDING_FINANCIALS_FILTER_SECTIONS = [
  {
    key: "company",
    label: "Bedrijf",
    options: [],
    color: "brick" as const,
    multiSelect: true,
    extractOptionsFromData: (rows: any[]) => {
      // Safety check: return empty array if rows is not an array
      if (!Array.isArray(rows)) {
        return [];
      }

      const companies = new Map();
      rows.forEach((row) => {
        if (row.company?.id && row.company?.name) {
          companies.set(row.company.id, row.company.name);
        }
      });
      return Array.from(companies.entries()).map(([id, name]) => ({
        key: id,
        label: name,
      }));
    },
  },
];

/**
 * Grouping options for building financials
 */
export const BUILDING_FINANCIALS_GROUPBY_OPTIONS = [
  { key: "none", label: "Geen" },
  {
    key: "company",
    label: "Per bedrijf",
    extractor: (building: any) => building.company?.name || "Geen bedrijf",
  },
];

/**
 * Extract company options from buildings
 */
export const extractCompanyOptions = (buildings: any[]) => {
  // Safety check: return empty array if buildings is not an array
  if (!Array.isArray(buildings)) {
    return [];
  }

  const companies = new Map();
  buildings.forEach((building) => {
    if (building.company?.id && building.company?.name) {
      companies.set(building.company.id, building.company.name);
    }
  });
  return Array.from(companies.entries()).map(([id, name]) => ({
    key: id,
    label: name,
  }));
};
